import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, MessageBar, MessageBarBody, Text, Textarea, makeStyles, tokens } from "@fluentui/react-components";
import { PersonName, normalisePhone } from "@gymplan/shared";
import { api } from "../lib/api.ts";
import { HeadsUp } from "./HeadsUp.tsx";

const useStyles = makeStyles({
  col: { display: "flex", flexDirection: "column", gap: "12px" },
  preview: { display: "grid", gridTemplateColumns: "1fr auto", gap: "2px 12px", fontSize: tokens.fontSizeBase200, maxHeight: "30vh", overflowY: "auto", padding: "8px 10px", borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground3 },
  bad: { color: tokens.colorPaletteRedForeground1 },
  hint: { color: tokens.colorNeutralForeground3 },
});

type Row = { line: string; name?: string; phone?: string; error?: string };

/** "Sam Jones, 07700 900123" / "Sam Jones 07700900123" / "07700 900123 Sam Jones" — one client per line. */
export function parseImport(text: string): Row[] {
  const seen = new Set<string>();
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line): Row => {
      const m = line.match(/(\+?[\d][\d\s().-]{6,}\d)/);
      if (!m) return { line, error: "no number" };
      const phone = normalisePhone(m[1]!);
      if (!phone) return { line, error: "number not recognised" };
      const name = line.replace(m[1]!, " ").replace(/[,;\t]+/g, " ").replace(/\s+/g, " ").trim();
      const ok = PersonName.safeParse(name);
      if (!ok.success || !/\S\s+\S/.test(name)) return { line, phone, error: "needs a first and last name" };
      if (seen.has(phone)) return { line, name: ok.data, phone, error: "duplicate" };
      seen.add(phone);
      return { line, name: ok.data, phone };
    });
}

/**
 * Trainer pastes a list of clients (name + mobile); RackUp invites each one on their behalf with a personal
 * sign-up link, then shows how many have joined on the home page. The trainer first confirms the clients
 * have been told to expect a message from RackUp (the heads-up is right there to copy).
 */
export function ImportClientsDialog({ open, onClose, trainerFirst, rackupNumber }: { open: boolean; onClose: () => void; trainerFirst: string; rackupNumber?: string | null }) {
  const s = useStyles();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [told, setTold] = useState(false);
  const rows = useMemo(() => parseImport(text), [text]);
  const good = rows.filter((r) => !r.error);
  const run = useMutation({
    mutationFn: () => api.post<{ created: { id: string; name: string }[]; skipped: { name: string; phone: string }[] }>("/api/clients/import", { clients: good.map((r) => ({ name: r.name, phone: r.phone })), clientsTold: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      void qc.invalidateQueries({ queryKey: ["clients-summary"] });
    },
  });
  const close = () => {
    run.reset();
    setText("");
    setTold(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && close()}>
      <DialogSurface style={{ maxWidth: 640 }}>
        <DialogBody>
          <DialogTitle>Import clients</DialogTitle>
          <DialogContent className={s.col}>
            {run.data ? (
              <>
                <MessageBar intent="success">
                  <MessageBarBody>
                    {run.data.created.length === 0 ? "Nobody new to invite." : `Invited ${run.data.created.length} client${run.data.created.length === 1 ? "" : "s"}: ${run.data.created.map((c) => c.name.split(" ")[0]).join(", ")}.`}
                    {run.data.skipped.length > 0 && ` Already on your list: ${run.data.skipped.map((c) => c.name.split(" ")[0]).join(", ")}.`}
                  </MessageBarBody>
                </MessageBar>
                <Text size={200} className={s.hint}>
                  Each one has a WhatsApp from RackUp, on your behalf, with their own sign-up link. You'll see who's joined on your home page, and can send one reminder after 48 hours.
                </Text>
              </>
            ) : (
              <>
                <Text size={200} className={s.hint}>
                  One client per line — name and mobile. RackUp WhatsApps each one a personal link to say when they can train; they aren't asked about any week until they've done that.
                </Text>
                <Textarea value={text} onChange={(_, d) => setText(d.value)} rows={6} resize="vertical" placeholder={"Sam Jones, 07700 900123\nPriya Patel 07700 900456"} aria-label="Clients to import" />
                {rows.length > 0 && (
                  <div className={s.preview} aria-label="Preview">
                    {rows.map((r, i) => (
                      <div key={i} style={{ display: "contents" }}>
                        <span className={r.error ? s.bad : undefined}>{r.name ?? r.line}</span>
                        <span className={r.error ? s.bad : s.hint}>{r.error ?? r.phone}</span>
                      </div>
                    ))}
                  </div>
                )}
                <HeadsUp trainerFirst={trainerFirst} rackupNumber={rackupNumber} compact />
                <Checkbox checked={told} onChange={(_, d) => setTold(!!d.checked)} label="I've told these clients that RackUp will message them on my behalf." />
                {run.error && (
                  <MessageBar intent="error">
                    <MessageBarBody>{(run.error as Error).message}</MessageBarBody>
                  </MessageBar>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {run.data ? (
              <Button appearance="primary" onClick={close}>Done</Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={close}>Cancel</Button>
                <Button appearance="primary" disabled={good.length === 0 || !told || run.isPending} onClick={() => run.mutate()}>
                  {run.isPending ? "Inviting…" : `Invite ${good.length || ""} client${good.length === 1 ? "" : "s"}`}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
